/* eslint-disable consistent-return */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-use-before-define */
/* eslint-disable no-plusplus */
/* eslint-disable no-await-in-loop */

import { message } from 'antd';
import { formatApiErrorMessages } from 'pages/utils';
import * as uploadService from 'services/transfer';
import { readFileChunkAsBase64 } from 'utils/upload';
import {
  isFileNameDuplicate,
  createUniqueFileName,
  handleDuplicateFolderName,
  showDuplicateConfirmModal,
  renameDuplicateFiles,
  isDuplicateError,
  checkDuplicateFolders,
} from './upload/duplicateHandler';

/**
 * 파일 업로드 관리자 생성 함수
 * 파일/폴더 업로드 로직을 처리하고 상태를 관리
 */
function createUploadManager() {
  /**
   * 상태 관리 객체 - 모든 상태 변수를 한 곳에서 관리
   */
  const state = {
    stateUpdater: null, // UI 상태 업데이트 함수
    uploadStartTime: {}, // 파일별 업로드 시작 시간
    lastProgressUpdate: {}, // 파일별 마지막 진행률 업데이트 시간
    abortControllers: {}, // 파일별 취소 컨트롤러
    activeTransfers: {}, // 전송 정보
    fileStatuses: {}, // 파일별 상태
    cancelledFiles: new Set(), // 취소된 파일 ID 집합
    allowSaveAsCopy: false, // 중복 파일 사본 저장 동의 여부
    isUploading: false, // 업로드 진행 중 여부
    userId: localStorage.getItem('userId') || '',
    userName: localStorage.getItem('username') || '',
    localeResource: null, // 로케일 리소스
  };

  /**
   * 파일 상태 업데이트 - 내부 상태와 UI 상태를 동시에 업데이트
   * @param {string} fileId - 파일 ID
   * @param {string} status - 상태 값 (waiting, uploading, completed, error, cancelled)
   */
  const updateFileStatus = (fileId, status) => {
    // 내부 상태 업데이트
    state.fileStatuses[fileId] = status;

    // UI 상태 업데이트
    if (state.stateUpdater) {
      state.stateUpdater.updateStatus(fileId, status);
    }
  };

  /**
   * 업로드 진행률 업데이트
   * @param {string} fileId - 파일 ID
   * @param {number} uploadedBytes - 업로드된 바이트 수
   * @param {number} totalBytes - 전체 바이트 수
   */
  const updateProgress = (fileId, uploadedBytes, totalBytes) => {
    if (!state.stateUpdater) return;

    const progress = Math.floor((uploadedBytes / totalBytes) * 100);

    // 속도 및 남은 시간 계산 (500ms마다)
    const now = Date.now();
    const lastUpdate = state.lastProgressUpdate[fileId] || { time: now, uploadedBytes: 0 };
    const timeDiff = now - lastUpdate.time;

    if (timeDiff > 500) {
      // 속도 계산 (bytes/sec)
      const bytesDiff = uploadedBytes - lastUpdate.uploadedBytes;
      const speedBps = bytesDiff / (timeDiff / 1000);

      state.stateUpdater.updateSpeed(fileId, speedBps);

      // 남은 시간 계산
      if (speedBps > 0) {
        const bytesRemaining = totalBytes - uploadedBytes;
        const secondsRemaining = bytesRemaining / speedBps;
        state.stateUpdater.updateTimeRemaining(fileId, secondsRemaining);
      }

      // 마지막 업데이트 정보 갱신
      state.lastProgressUpdate[fileId] = {
        time: now,
        uploadedBytes,
      };
    }

    // 진행 상황 업데이트
    state.stateUpdater.updateProgress(fileId, {
      progress,
      uploadedBytes,
      totalBytes,
    });
  };

  /**
   * 파일 업로드 준비를 위한 검증 객체 생성
   * @param {FileList|Array} files - 업로드할 파일 목록
   * @returns {Array} 검증용 파일 객체 배열
   */
  const filesForValidation = (files) => {
    return Array.from(files).map((file) => ({
      fileName: file.name,
      fileSize: file.size,
      filePath: file.webkitRelativePath || file.name,
      originFile: file,
    }));
  };

  /**
   * 파일을 청크 단위로 업로드
   * @param {Object} file - 업로드할 파일 객체
   * @param {string} transferId - 전송 ID
   * @param {string} destination - 업로드 대상 경로
   * @param {Object} drive - 드라이브 정보
   * @returns {Promise<Object>} 업로드 결과
   */
  const uploadFile = async (file, transferId, destination, drive) => {
    // 취소된 파일이면 업로드 시작도 하지 않음
    if (state.cancelledFiles.has(file.uid)) {
      return {
        success: false,
        fileId: file.uid,
        fileName: file.name,
        error: 'Upload cancelled',
      };
    }

    // 업로드 시작 시간 기록
    const startTime = Date.now();
    state.uploadStartTime[file.uid] = startTime;
    state.lastProgressUpdate[file.uid] = {
      time: startTime,
      uploadedBytes: 0,
    };

    // AbortController 생성 (업로드 취소용)
    // https://developer.mozilla.org/ko/docs/Web/API/AbortController
    const controller = new AbortController();
    state.abortControllers[file.uid] = controller;

    const chunkSize = 50 * 1024 * 1024;

    // 청크 개수 계산
    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
    let uploadedBytes = 0;

    // 업로드 상태 업데이트
    updateFileStatus(file.uid, 'uploading');
    if (state.stateUpdater) {
      state.stateUpdater.updateProgress(file.uid, {
        progress: 0,
        uploadedChunks: 0,
        totalChunks,
        uploadedBytes: 0,
        totalBytes: file.size,
      });
    }

    try {
      // 청크별 업로드
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        // 업로드 중단 확인
        if (controller.signal.aborted || state.cancelledFiles.has(file.uid)) {
          throw new Error('Upload cancelled');
        }

        // 청크 범위 계산
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);

        // 청크 데이터 읽기
        let chunk;
        if (file.size === 0) {
          // 0바이트 파일은 빈 문자열로 처리
          chunk = '';
        } else {
          chunk = await readFileChunkAsBase64(file.originFileObj, start, end);
        }
        const chunkData = {
          index: chunkIndex,
          totalChunks,
          data: chunk,
        };

        // 업로드 데이터 준비
        const uploadData = {
          transferId,
          destPath: destination,
          driveId: drive.driveId,
          driveName: drive.driveName,
          userId: state.userId,
        };

        const fileData = {
          fileName: file.name,
          filePath: file.path,
        };

        // 업로드 중단 재확인 (API 호출 직전)
        if (controller.signal.aborted || state.cancelledFiles.has(file.uid)) {
          throw new Error('Upload cancelled');
        }

        // 청크 업로드
        const { data: chunkResponse } = await uploadService.uploadChunk({
          uploadData,
          fileData,
          chunkData,
        });

        if (!chunkResponse.success) {
          throw new Error(chunkResponse.errorMessage || 'Chunk upload failed');
        }

        // 업로드된 바이트 계산
        uploadedBytes += end - start;

        // 진행 상황 업데이트
        updateProgress(file.uid, uploadedBytes, file.size);
      }

      // 업로드 성공 처리
      updateFileStatus(file.uid, 'completed');

      return {
        success: true,
        fileId: file.uid,
        fileName: file.name,
      };
    } catch (error) {
      // 사용자가 취소한 경우 취소 상태로, 그렇지 않으면 오류 상태로 설정
      if (controller.signal.aborted || state.cancelledFiles.has(file.uid)) {
        updateFileStatus(file.uid, 'cancelled');
      } else {
        updateFileStatus(file.uid, 'error');
      }

      return {
        success: false,
        fileId: file.uid,
        fileName: file.name,
        error: error.message,
      };
    } finally {
      // 리소스 정리
      delete state.abortControllers[file.uid];
    }
  };

  /**
   * 업로드 과정 시작 - 여러 파일을 순차적으로 업로드
   * @param {Array} items - 업로드할 항목 배열
   * @param {string} destination - 업로드 대상 경로
   * @param {Object} drive - 드라이브 정보
   * @param {string} transferId - 전송 ID
   */
  const startUploadProcess = async (items, destination, drive, transferId) => {
    if (!items || items.length === 0) {
      return;
    }

    state.isUploading = true;

    try {
      // transferId 추적
      state.activeTransfers[transferId] = {
        items: items.map((item) => item.uid),
        status: 'active',
      };

      // 각 파일 업로드
      const uploadResults = [];
      for (const item of items) {
        // 이미 취소된 항목은 건너뛰기
        if (state.cancelledFiles.has(item.uid)) {
          continue;
        }
        const result = await uploadFile(item, transferId, destination, drive);
        uploadResults.push(result);
      }

      // 성공적으로 업로드된 파일 필터링
      const successfulFiles = uploadResults
        .filter((result) => result.success)
        .map((result) => {
          const originalItem = items.find((item) => item.uid === result.fileId);
          return {
            fileName: originalItem.name,
            filePath: originalItem.path,
            fileSize: originalItem.size,
          };
        });

      // 업로드 완료 API 호출
      if (successfulFiles.length > 0) {
        await completeUpload(successfulFiles, transferId, destination, drive);
      }
    } catch (error) {
      console.error('업로드 실패:', error);
      message.error(state.localeResource[error.message || 'txt.file-transfer.msg-upload-failed']);

      items.forEach((item) => {
        if (!state.cancelledFiles.has(item.uid)) {
          updateFileStatus(item.uid, 'error');
        }
      });
    } finally {
      state.isUploading = false;
      // 현재 배치 종료 후 중복 허용 플래그 초기화
      state.allowSaveAsCopy = false;

      // 다음 배치 처리 (대기 중인 항목 찾기)
      if (state.stateUpdater && state.stateUpdater.getItems) {
        const waitingItems = state.stateUpdater
          .getItems()
          .filter(
            (item) =>
              state.fileStatuses[item.uid] === 'waiting' && !state.cancelledFiles.has(item.uid),
          );

        if (waitingItems.length > 0) {
          setTimeout(() => startUploadProcess(waitingItems, destination, drive), 1000);
        }
      }
    }
  };

  /**
   * 업로드 완료 처리
   * @param {Array} successfulFiles - 성공적으로 업로드된 파일 목록
   * @param {string} transferId - 전송 ID
   * @param {string} destination - 업로드 대상 경로
   * @param {Object} drive - 드라이브 정보
   */
  const completeUpload = async (successfulFiles, transferId, destination, drive) => {
    try {
      const { data: completeResult } = await uploadService.completeUpload({
        uploadData: {
          transferId,
          destPath: destination,
          driveId: drive.driveId,
          driveName: drive.driveName,
          userId: state.userId,
        },
        files: successfulFiles,
      });

      if (completeResult.success) {
        // transferId 상태 업데이트
        state.activeTransfers[transferId] = {
          ...state.activeTransfers[transferId],
          status: 'completed',
        };

        // 성공 메시지 표시
        const totalUploaded = successfulFiles.length;
        const expectedTotal = state.activeTransfers[transferId]?.items?.length || 0;

        if (totalUploaded === expectedTotal) {
          message.success(state.localeResource['txt.file-transfer.msg-all-uploads-complete']);
        } else {
          message.warning(state.localeResource['txt.file-transfer.msg-some-uploads-complete']);
        }

        // 완료 이벤트 발생 - 디렉토리 내용 갱신 트리거
        const refreshEvent = new CustomEvent('uploadComplete', {
          detail: { path: destination },
        });
        window.dispatchEvent(refreshEvent);
      } else {
        message.error(
          state.localeResource[
            completeResult.errorMessage || 'txt.file-transfer.msg-upload-complete-failed'
          ],
        );
      }
    } catch (error) {
      console.error('업로드 완료 처리 오류:', error);
      throw error; // 상위 함수에서 처리하도록 오류 전파
    }
  };

  /**
   * 업로드 검증 및 준비
   * @param {Array} validatedFiles - 검증된 파일 목록
   * @param {string} destination - 업로드 대상 경로
   * @param {Object} drive - 드라이브 정보
   * @param {Array} currentDirectoryFiles - 현재 디렉토리 파일 목록 (중복 확인용)
   */
  const prepareAndStartUpload = async (
    validatedFiles,
    destination,
    drive,
    currentDirectoryFiles,
  ) => {
    try {
      // API에 전송할 파일 데이터 변환
      const filesForUpload = validatedFiles.map((item) => ({
        fileName: item.fileName,
        fileSize: item.fileSize,
        filePath: item.filePath,
      }));

      // 업로드 요청 API 호출
      const { data: result } = await uploadService.requestUpload({
        files: filesForUpload,
        destPath: destination,
        driveId: drive.driveId,
        driveName: drive.driveName,
        userId: state.userId,
        userName: state.userName,
      });

      // 오류 처리
      if (!result.success) {
        await handleValidationError(
          result,
          validatedFiles,
          destination,
          drive,
          currentDirectoryFiles,
        );
        return;
      }

      // 검증 성공 - 업로드 프로세스 시작
      const { transferId } = result.data;
      await processValidatedFiles(validatedFiles, transferId, destination, drive);

      return true;
    } catch (error) {
      console.error('업로드 요청 오류:', error);
      message.error(state.localeResource['txt.file-transfer.msg-validation-failed']);
      return false;
    }
  };

  /**
   * 백엔드 오류 처리 (중복 파일 등)
   */
  const handleValidationError = async (
    result,
    validatedFiles,
    destination,
    drive,
    currentDirectoryFiles,
  ) => {
    // 백엔드 중복 파일 벨리데이션 에러이면..
    if (isDuplicateError({ data: result })) {
      // 사용자가 프론트 벨리데이션에서 이미 중복 허용에 동의했는지 확인한다..
      if (state.allowSaveAsCopy) {
        // 중복 파일 이름 변경 재시도
        await retryWithRenamedFiles(validatedFiles, destination, drive, currentDirectoryFiles);
      } else {
        const confirmed = await showDuplicateConfirmModal(state.localeResource);
        if (confirmed) {
          state.allowSaveAsCopy = true;
          await prepareAndStartUpload(validatedFiles, destination, drive, currentDirectoryFiles);
        }
      }
      return;
    }

    if (result.errorType === 'general') {
      message.error(
        state.localeResource[result.errorMessage || 'txt.file-transfer.msg-unknown-error'],
      );
    }

    if (result.errorType === 'validation') {
      const errorMessages = formatApiErrorMessages(result, state.localeResource);
      errorMessages.forEach((msg) => {
        message.error(msg);
      });
    }
  };

  /**
   * 중복 허용 상태에서 파일명을 변경하여 재시도
   */
  const retryWithRenamedFiles = async (
    validatedFiles,
    destination,
    drive,
    currentDirectoryFiles,
  ) => {
    try {
      // 디렉토리 목록 새로 조회
      const refreshResponse = await uploadService.getDirectoryContents(destination);
      let updatedDirectoryFiles = currentDirectoryFiles;

      if (refreshResponse.data && refreshResponse.data.success) {
        updatedDirectoryFiles = refreshResponse.data.data || [];
      }

      const updatedFiles = [...validatedFiles];

      for (const item of updatedFiles) {
        const newFileName = createUniqueFileName(item.fileName, updatedDirectoryFiles);
        item.fileName = newFileName;

        if (item.filePath && item.filePath.includes('/')) {
          const lastSlashIndex = item.filePath.lastIndexOf('/');
          item.filePath = item.filePath.substring(0, lastSlashIndex + 1) + newFileName;
        } else {
          item.filePath = newFileName;
        }

        updatedDirectoryFiles.push({ name: newFileName, type: 'file' });
      }

      // 재귀적으로 다시 시도
      await prepareAndStartUpload(updatedFiles, destination, drive, updatedDirectoryFiles);
    } catch (error) {
      console.error('파일명 변경 재시도 오류:', error);
      message.error(state.localeResource['txt.file-transfer.msg-validation-failed']);
    }
  };

  /**
   * 검증된 파일들을 UI에 표시하고 업로드 프로세스 시작
   */
  const processValidatedFiles = async (validatedFiles, transferId, destination, drive) => {
    // UI에 표시할 항목 생성
    const uploadItems = validatedFiles.map((item) => {
      const uid = `upload_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

      // 초기 상태 설정
      state.fileStatuses[uid] = 'waiting';

      return {
        uid,
        name: item.fileName,
        size: item.fileSize,
        type: item.originFile.type,
        originFileObj: item.originFile,
        isFolder: item.filePath !== item.fileName, // 경로가 파일명과 다르면 폴더다.
        path: item.filePath,
        status: 'waiting',
        transferId,
      };
    });

    // 상태 업데이터가 설정되어 있으면 아이템 추가
    if (state.stateUpdater) {
      state.stateUpdater.addItems(uploadItems);

      // 각 항목 초기 상태 설정
      uploadItems.forEach((item) => {
        state.stateUpdater.updateStatus(item.uid, 'waiting');
        state.stateUpdater.updateProgress(item.uid, {
          progress: 0,
          uploadedChunks: 0,
          totalChunks: 0,
          uploadedBytes: 0,
          totalBytes: item.size,
        });
      });

      // 패널 표시
      if (uploadItems.length > 0 && typeof state.stateUpdater.toggleVisibility === 'function') {
        state.stateUpdater.toggleVisibility(true);
      }
    }

    // 패널에 표시 후 실제 업로드 시작
    await startUploadProcess(uploadItems, destination, drive, transferId);
  };

  /**
   * 메인 파일 업로드 함수
   * @param {FileList|Array} files - 업로드할 파일 목록
   * @param {string} destination - 업로드 대상 경로
   * @param {Object} drive - 드라이브 정보
   * @param {boolean} isFolder - 폴더 업로드 여부
   * @returns {number} 업로드 예정 파일 개수
   */
  const addFiles = (files, destination, drive, isFolder = false) => {
    if (!drive) {
      message.warning(state.localeResource['txt.file-transfer.msg-select-drive']);
      return 0;
    }

    // 업로드 요청 파일 준비
    const filesToValidate = filesForValidation(files);

    // 디렉토리 내 파일 목록 조회 및 검증 과정 시작
    uploadService
      .getDirectoryContents(destination)
      .then(({ data: directoryResponse }) => {
        // 현재 디렉토리 파일 목록 저장
        let currentDirectoryFiles = [];
        if (directoryResponse && directoryResponse.success && directoryResponse.data) {
          currentDirectoryFiles = directoryResponse.data;
        }

        // 중복 파일 찾기
        const duplicateItems = filesToValidate.filter((item) =>
          isFileNameDuplicate(item.fileName, currentDirectoryFiles, item.filePath, isFolder),
        );

        // 중복 파일/폴더 처리 및 업로드 실행
        processDuplicatesAndUpload(
          filesToValidate,
          duplicateItems,
          currentDirectoryFiles,
          destination,
          drive,
          isFolder,
        );
      })
      .catch((error) => {
        console.error('디렉토리 내용 조회 실패:', error);
        message.error(state.localeResource['txt.file-transfer.msg-validation-failed']);
      });

    // 일단 파일 개수 반환 (비동기 완료 전이라 실제 업로드 개수와 다를 수 있음)
    return files.length;
  };

  /**
   * 중복 파일/폴더 처리 및 업로드 시작
   */
  const processDuplicatesAndUpload = async (
    filesToValidate,
    duplicateItems,
    currentDirectoryFiles,
    destination,
    drive,
    isFolder,
  ) => {
    // 중복 파일 또는 폴더 체크
    const { hasDuplicates: hasDuplicateFolders } = checkDuplicateFolders(
      filesToValidate,
      currentDirectoryFiles,
      isFolder,
    );
    // 중복 파일이 있고 아직 사용자가 동의하지 않은 경우
    const duplicateItems1 = duplicateItems.filter((item) => {
      if (isFolder && item.filePath && item.filePath.includes('/')) {
        return false; // 폴더 내 파일은 중복 체크에서 제외
      }
      return true; // 루트 레벨 파일은 중복 체크
    });

    if ((duplicateItems1.length > 0 || hasDuplicateFolders) && !state.allowSaveAsCopy) {
      const confirmed = await showDuplicateConfirmModal(state.localeResource);

      if (!confirmed) {
        // 사용자가 사본 업로드 취소
        return;
      }

      // 사용자가 확인한 경우 중복 허용 플래그 설정
      state.allowSaveAsCopy = true;
    }

    // 중복 파일 이름 변경 처리
    let processedFiles = filesToValidate;
    let updatedDirectoryFiles = [...currentDirectoryFiles];

    if (state.allowSaveAsCopy && duplicateItems1.length > 0) {
      // 중복 파일들의 이름 변경
      const result = renameDuplicateFiles(duplicateItems1, currentDirectoryFiles);

      // 변경된 파일명으로 원본 파일 목록 업데이트
      processedFiles = filesToValidate.map((file) => {
        const duplicateItem = duplicateItems1.find(
          (item) =>
            item.fileName === file.fileName &&
            (!isFolder || !file.filePath.includes('/') || item.filePath === file.filePath),
        );

        if (duplicateItem) {
          const updatedItem = result.updatedFiles.find(
            (item) => item.fileName === duplicateItem.fileName,
          );
          return updatedItem || file;
        }
        return file;
      });

      updatedDirectoryFiles = result.updatedDirectoryFiles;
    }

    // 폴더명 중복 처리
    const finalFiles = handleDuplicateFolderName(processedFiles, updatedDirectoryFiles, isFolder);

    // 업로드 시작
    await prepareAndStartUpload(finalFiles, destination, drive, updatedDirectoryFiles);
  };

  /**
   * 업로드 취소
   * @param {string} fileId - 파일 ID
   * @returns {boolean} 취소 성공 여부
   */
  const cancelUpload = (fileId) => {
    state.cancelledFiles.add(fileId);

    // AbortController를 통해 진행 중인 요청 중단
    if (state.abortControllers[fileId]) {
      state.abortControllers[fileId].abort();
      delete state.abortControllers[fileId];
    }

    // 업로드 상태 업데이트
    updateFileStatus(fileId, 'cancelled');
    return true;
  };

  /**
   * 모든 업로드 취소
   * @returns {boolean} 취소 성공 여부
   */
  const cancelAllUploads = () => {
    // 모든 파일 목록 가져오기
    const allFiles =
      state.stateUpdater && state.stateUpdater.getItems ? state.stateUpdater.getItems() : [];

    // 모든 파일을 취소 목록에 추가
    allFiles.forEach((file) => {
      state.cancelledFiles.add(file.uid);

      // AbortController를 통해 진행 중인 요청 중단
      if (state.abortControllers[file.uid]) {
        state.abortControllers[file.uid].abort();
        delete state.abortControllers[file.uid];
      }

      // 상태 업데이트
      updateFileStatus(file.uid, 'cancelled');
    });

    state.isUploading = false;
    message.info(state.localeResource['txt.file-transfer.msg-all-uploads-cancelled']);

    return true;
  };

  /**
   * 파일 상태 확인 (완료, 취소, 오류 상태 확인)
   * @param {string} fileId - 파일 ID
   * @returns {boolean} 파일이 종료 상태인지 여부
   */
  const isFileFinished = (fileId) => {
    const status = state.fileStatuses[fileId];
    return status === 'cancelled' || status === 'error' || status === 'completed';
  };

  return {
    // 상태 업데이터 설정
    setStateUpdater: (updater) => {
      state.stateUpdater = updater;
    },

    // 로케일 리소스 설정
    setLocaleResource: (resource) => {
      state.localeResource = resource;
    },

    // 사용자 정보 설정
    setUserInfo: (newUserId, newUserName) => {
      state.userId = newUserId;
      state.userName = newUserName;
    },

    // 파일 추가
    addFiles,

    // 업로드 취소
    cancelUpload,
    cancelAllUploads,

    // 진행 중인 업로드가 있는지 확인
    hasActiveUploads: () => {
      return state.isUploading;
    },

    // 완료된 파일들 정리 (UI에서 완료 항목 제거 시 호출)
    clearCompleted: () => {
      if (state.stateUpdater && state.stateUpdater.getItems) {
        const items = state.stateUpdater.getItems();
        items.forEach((item) => {
          if (isFileFinished(item.uid)) {
            // 완료된 파일은 내부 상태에서도 제거
            delete state.fileStatuses[item.uid];
            // 취소된 파일 목록에서도 제거
            state.cancelledFiles.delete(item.uid);
          }
        });
      }
    },
  };
}

// 인스턴스 생성 및 내보내기
const uploadManagerInstance = createUploadManager();
export default uploadManagerInstance;
